import React, { useCallback, useEffect, useState } from 'react';

import { Star, StarOutline } from '@mui/icons-material';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import { debounce } from 'src/utils/general';

interface FilterState {
  operacao: string;
  mercado: string;
  tipoAssembleia: string;
  favoritos: boolean;
  especiedocumento: string;
  categoriadocumento: string;
}

interface TimelineFiltersProps {
  filtersData: {
    categories: string[];
    types: string[];
    species: string[];
    markets: string[];
  };
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
}

const assembleiaTypes = [
  { value: 'AGE', label: 'Assembleia Extraordinária' },
  { value: 'AGO', label: 'Assembleia Ordinária' },
  { value: 'AGO/E', label: 'Assembleia Especial' },
];

export function TimelineFilters({
  filtersData,
  filters,
  onFiltersChange,
}: TimelineFiltersProps) {
  const [operation, setOperation] = useState<string>('');
  const [market, setMarket] = useState<string>('All');
  const [type, setType] = useState<string>('All');
  const [categories, setCategories] = useState<string>('All');
  const [species, setSpecies] = useState<string>('All');
  const [favorites, setFavorites] = useState<boolean>(false);

  useEffect(() => {
    setOperation(filters.operacao || '');
  }, [filters.operacao]);

  useEffect(() => {
    setMarket(filters.mercado || 'All');
  }, [filters.mercado]);

  useEffect(() => {
    setType(filters.tipoAssembleia || 'All');
  }, [filters.tipoAssembleia]);

  useEffect(() => {
    setCategories(filters.categoriadocumento || 'All');
  }, [filters.categoriadocumento]);

  useEffect(() => {
    setSpecies(filters.especiedocumento || 'All');
  }, [filters.especiedocumento]);

  useEffect(() => {
    setFavorites(filters.favoritos);
  }, [filters.favoritos]);

  const debouncedHandleFilterOperacao = useCallback(
    debounce((value: string) => {
      handleFilterChange('operacao', value);
    }, 700),
    [filters],
  );

  const handleFilterChange = (field: keyof FilterState, value: any) => {
    const newFilters = { ...filters, [field]: value };
    onFiltersChange(newFilters);
  };

  const handleOperationChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value;
    setOperation(value);
    debouncedHandleFilterOperacao(value);
  };

  const handleMarketChange = (event: SelectChangeEvent) => {
    setMarket(event.target.value);
    handleFilterChange(
      'mercado',
      event.target.value === 'All' ? null : event.target.value,
    );
  };

  const handleTypeChange = (event: SelectChangeEvent) => {
    setType(event.target.value);
    handleFilterChange(
      'tipoAssembleia',
      event.target.value === 'All' ? null : event.target.value,
    );
  };

  const handleSpeciesChange = (event: SelectChangeEvent) => {
    setSpecies(event.target.value);
    handleFilterChange(
      'especiedocumento',
      event.target.value === 'All' ? null : event.target.value,
    );
  };

  const handleCategoriesChange = (event: SelectChangeEvent) => {
    setCategories(event.target.value);
    handleFilterChange(
      'categoriadocumento',
      event.target.value === 'All' ? null : event.target.value,
    );
  };

  const handleFavoritesToggle = () => {
    setFavorites(!favorites);
    handleFilterChange('favoritos', !favorites);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: { xs: 'center', sm: 'flex-start' },
        alignItems: { xs: 'flex-start', sm: 'center' },
        gap: 2,
        padding: 2,
        backgroundColor: '#fff',
        borderRadius: 1,
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      }}>
      <TextField
        label="Operação"
        placeholder="Nome, ISIN, CNPJ,etc..."
        variant="outlined"
        size="small"
        value={operation}
        onChange={handleOperationChange}
        sx={{ minWidth: 200, width: { xs: '100%', sm: 'auto' } }}
      />

      <FormControl
        variant="outlined"
        sx={{ minWidth: 120, width: { xs: '100%', sm: 'auto' } }}
        size="small">
        <InputLabel id="mercado-label">Mercado</InputLabel>
        <Select
          labelId="mercado-label"
          value={market}
          onChange={handleMarketChange}
          label="Mercado">
          <MenuItem value="All">Todos</MenuItem>
          {filtersData.markets.map((market) => (
            <MenuItem
              key={market}
              value={market}
              sx={{ textTransform: 'uppercase' }}>
              {market?.toUpperCase()}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl
        variant="outlined"
        sx={{ minWidth: 180, width: { xs: '100%', sm: 'auto' } }}
        size="small">
        <InputLabel id="tipo-assembleia-label">Categoria</InputLabel>
        <Select
          labelId="tipo-assembleia-label"
          value={categories}
          onChange={handleCategoriesChange}
          label="Categoria">
          <MenuItem value="All">Todas</MenuItem>
          {filtersData.categories.map((category) => (
            <MenuItem key={category} value={category}>
              {category}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl
        variant="outlined"
        sx={{ minWidth: 180, width: { xs: '100%', sm: 'auto' } }}
        size="small">
        <InputLabel id="tipo-assembleia-label">Tipo Assembleia</InputLabel>
        <Select
          labelId="tipo-assembleia-label"
          value={type}
          onChange={handleTypeChange}
          label="Tipo Assembleia">
          <MenuItem value="All">Todos</MenuItem>
          {filtersData.types.map((type) => (
            <MenuItem key={type} value={type}>
              {assembleiaTypes.find((t) => t.value === type)?.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl
        variant="outlined"
        sx={{ minWidth: 180, width: { xs: '100%', sm: 'auto' } }}
        size="small">
        <InputLabel id="especie-documento-label">Espécie Documento</InputLabel>
        <Select
          labelId="especie-documento-label"
          value={species}
          onChange={handleSpeciesChange}
          label="Especie Documento">
          <MenuItem value="All">Todas</MenuItem>
          {filtersData.species.map((species) => (
            <MenuItem key={species} value={species}>
              {species}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Button
        variant={favorites ? 'contained' : 'outlined'}
        onClick={handleFavoritesToggle}
        startIcon={
          favorites ? (
            <Star fontSize="small" />
          ) : (
            <StarOutline fontSize="small" />
          )
        }
        sx={{
          borderColor: '#FF8211',
          color: favorites ? '#fff' : '#FF8211',
          backgroundColor: favorites ? '#FF8211' : 'transparent',
          '&:hover': {
            backgroundColor: favorites ? '#e67300' : 'rgba(255, 130, 17, 0.04)',
            borderColor: '#FF8211',
          },
          textTransform: 'none',
          fontWeight: 500,
          minWidth: 120,
        }}>
        <Typography variant="body2" fontWeight="medium">
          Favoritos
        </Typography>
      </Button>
    </Box>
  );
}
